import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { fmtUGX } from "@/lib/format";
import {
  DollarSign, Percent, TrendingUp, Users, Building2, Sparkles,
} from "lucide-react";

type FeeModel = "customer" | "merchant" | "shared";

const KACYBER_RATE = 0.02;
const PLATFORM_RATE = 0.028;

const PRESETS = [5000, 50000, 250000, 1000000];

export function PricingEngineDemo() {
  const [amount, setAmount] = useState(50000);
  const [model, setModel] = useState<FeeModel>("customer");

  const calc = useMemo(() => {
    const platformFee = Math.round(amount * PLATFORM_RATE);
    const kacyberCost = Math.round(amount * KACYBER_RATE);
    const profit = platformFee - kacyberCost;

    let customerFee = 0;
    let merchantReceives = amount;

    if (model === "customer") {
      customerFee = platformFee;
      merchantReceives = amount;
    } else if (model === "merchant") {
      customerFee = 0;
      merchantReceives = amount - platformFee;
    } else {
      customerFee = Math.round(platformFee / 2);
      merchantReceives = amount - Math.round(platformFee / 2);
    }

    return { platformFee, kacyberCost, profit, customerFee, merchantReceives };
  }, [amount, model]);

  const maxBar = amount + calc.platformFee;

  return (
    <Card className="overflow-hidden p-6 sm:p-8">
      <div className="mb-6">
        <Badge variant="outline">
          <Sparkles className="mr-1 h-3 w-3" /> Pricing Engine
        </Badge>
        <h3 className="mt-2 font-display text-2xl">Smart Fee Optimization</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Configurable pricing per merchant. KaCyberPay charges 2% — TriSpherePay charges 2.8%.
          The 0.8% spread is platform revenue.
        </p>
      </div>

      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between">
          <label className="text-xs uppercase tracking-wider text-muted-foreground">Transaction amount</label>
          <span className="font-display text-2xl">{fmtUGX(amount)}</span>
        </div>
        <Slider
          value={[amount]}
          onValueChange={(v) => setAmount(v[0])}
          min={5000}
          max={1000000}
          step={5000}
          className="py-2"
        />
        <div className="mt-2 flex gap-2">
          {PRESETS.map((p) => (
            <Button
              key={p}
              variant={amount === p ? "default" : "outline"}
              size="sm"
              onClick={() => setAmount(p)}
              className="text-xs"
            >
              {fmtUGX(p)}
            </Button>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <label className="mb-2 block text-xs uppercase tracking-wider text-muted-foreground">Fee model</label>
        <div className="grid grid-cols-3 gap-2">
          {(
            [
              { k: "customer", label: "Customer pays", desc: "Full fee on customer" },
              { k: "merchant", label: "Merchant absorbs", desc: "Fee from merchant" },
              { k: "shared", label: "Shared", desc: "50 / 50 split" },
            ] as const
          ).map((m) => (
            <button
              key={m.k}
              onClick={() => setModel(m.k)}
              className={`rounded-lg border p-3 text-left transition ${
                model === m.k ? "border-[var(--forest)] bg-[var(--forest)]/10" : "border-border hover:bg-muted/50"
              }`}
            >
              <div className="text-sm font-medium">{m.label}</div>
              <div className="text-[11px] text-muted-foreground">{m.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <ResultCard icon={Users} label="Customer fee" value={fmtUGX(calc.customerFee)} tone="blue" />
        <ResultCard icon={Building2} label="Merchant receives" value={fmtUGX(calc.merchantReceives)} tone="forest" />
        <ResultCard icon={Percent} label="KaCyberPay cost" value={fmtUGX(calc.kacyberCost)} sub="2% provider fee" tone="rose" />
        <ResultCard icon={TrendingUp} label="Platform profit" value={fmtUGX(calc.profit)} sub="0.8% net margin" tone="mustard" />
      </div>

      <div className="mt-6 space-y-2">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Revenue breakdown</div>
        <div className="flex h-8 overflow-hidden rounded-lg">
          <motion.div
            className="flex items-center justify-center bg-blue-500/80 text-[10px] font-medium text-white"
            initial={{ width: 0 }}
            animate={{ width: `${(calc.customerFee / maxBar) * 100}%` }}
            transition={{ duration: 0.4 }}
          >
            {calc.customerFee > 0 && fmtUGX(calc.customerFee)}
          </motion.div>
          <motion.div
            className="flex items-center justify-center bg-emerald-600/80 text-[10px] font-medium text-white"
            initial={{ width: 0 }}
            animate={{ width: `${(calc.merchantReceives / maxBar) * 100}%` }}
            transition={{ duration: 0.4 }}
          >
            {fmtUGX(calc.merchantReceives)}
          </motion.div>
          <motion.div
            className="flex items-center justify-center bg-amber-500/80 text-[10px] font-medium text-white"
            initial={{ width: 0 }}
            animate={{ width: `${(calc.profit / maxBar) * 100}%` }}
            transition={{ duration: 0.4 }}
          >
            {calc.profit > 0 && fmtUGX(calc.profit)}
          </motion.div>
        </div>
        <div className="flex gap-4 text-[11px]">
          <Legend color="bg-blue-500/80" label="Customer fee" />
          <Legend color="bg-emerald-600/80" label="Merchant receives" />
          <Legend color="bg-amber-500/80" label="Platform profit" />
        </div>
      </div>
    </Card>
  );
}

function ResultCard({
  icon: Icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: typeof DollarSign;
  label: string;
  value: string;
  sub?: string;
  tone: "blue" | "forest" | "rose" | "mustard";
}) {
  const tones: Record<string, string> = {
    blue: "text-blue-600 bg-blue-50",
    forest: "text-emerald-600 bg-emerald-50",
    rose: "text-rose-600 bg-rose-50",
    mustard: "text-amber-600 bg-amber-50",
  };
  return (
    <div className="rounded-xl border p-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <div className={`grid h-7 w-7 place-items-center rounded-lg ${tones[tone]}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      <div className="mt-1.5 font-display text-lg">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className={`h-2.5 w-2.5 rounded ${color}`} />
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}
